import { ChainData } from "@0xsquid/squid-types";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { keys } from "../../core/queries/queries-keys";
import { HistoryTxType } from "../../core/types/history";
import { SendTransactionStatus } from "../../core/types/transaction";
import { getSendTransactionStatus } from "../../services/internal/sendTransactionStatus";
import {
  getSendTxStatusRefetchInterval,
  isHistoryTransactionEnded,
} from "../../services/internal/transactionStatusService";
import { useHistoryStore } from "../store/useHistoryStore";

interface UseSendTransactionStatusParams {
  chain: ChainData | undefined;
  txHash: string | undefined;
}

interface UseSendTransactionStatusReturnType {
  status: SendTransactionStatus | undefined;
}

export function useSendTransactionStatus({
  chain,
  txHash,
}: UseSendTransactionStatusParams): UseSendTransactionStatusReturnType {
  const [isTransactionComplete, setIsTransactionComplete] = useState(false);
  const replaceTransactionStatus = useHistoryStore(
    (state) => state.replaceTransactionStatus
  );
  const findTransaction = useHistoryStore((state) => state.findTransaction);

  const currentHistoryItem = findTransaction({
    transactionId: txHash,
    txType: HistoryTxType.SEND,
  });

  const { data: status } = useQuery<SendTransactionStatus | undefined>({
    queryKey: keys().sendTransactionStatus(txHash, chain?.chainId),
    queryFn: async () => {
      if (!chain || !txHash) throw new Error("Chain or txHash not provided");

      return getSendTransactionStatus({
        chain,
        txHash,
      });
    },
    onSuccess: (txStatus) => {
      setIsTransactionComplete(txStatus !== SendTransactionStatus.ONGOING);

      if (txStatus != null && !!txHash) {
        replaceTransactionStatus({
          txType: HistoryTxType.SEND,
          status: txStatus,
          hash: txHash,
        });
      }
    },
    onError() {
      setIsTransactionComplete(true);

      if (txHash) {
        replaceTransactionStatus({
          txType: HistoryTxType.SEND,
          status: SendTransactionStatus.ERROR,
          hash: txHash,
        });
      }
    },
    refetchInterval: chain
      ? getSendTxStatusRefetchInterval(chain.chainType)
      : false,
    enabled:
      !!chain &&
      !!txHash &&
      !isTransactionComplete &&
      !!currentHistoryItem &&
      !isHistoryTransactionEnded({
        data: currentHistoryItem?.data,
        txType: HistoryTxType.SEND,
      }),
  });

  return {
    status: status ?? currentHistoryItem?.data?.status,
  };
}
